import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {DomainToolCompany} from '@app/model/domain-tool/dcp-company.model';
import {DcpCustomerRegistrationDecoratorService} from '@app/services/dcp-customer-registration-decorator.service';
import {DomainToolService} from '@app/services/registration/domain-tool.service';
import {UserNameValidationService} from '@app/services/user-registration/user-name-validation.service';
import {UserRegistrationConfigService} from '@app/services/user-registration/user-registration-config.service';
import {UserRegistrationRedirectService} from '@app/services/user-registration/user-registration-redirect.service';
import {DcpUserService} from '@app/services/user/dcp-user.service';
import {DcpError, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {ComboBoxDropdownComponent} from 'ng-design-system';
import {combineLatest} from 'rxjs';
import {first} from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'dcp-company-selection',
  templateUrl: './company-selection.component.html',
  styleUrls: ['./company-selection.component.scss']
})
export class CompanySelectionComponent implements OnInit {

  @ViewChild('comboBoxDropdown') comboBoxDropdown: ComboBoxDropdownComponent;
  companies: DomainToolCompany[];
  selectedCompany: DomainToolCompany;
  showErrors: boolean = true;
  showContactUsButton: boolean = false;
  autoBpCreationError: DcpError;
  isRegularRegistrationFlow: boolean = true;

  constructor(
    private userNameValidationService: UserNameValidationService,
    private domainToolService: DomainToolService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private userAccountFacade: UserAccountFacade,
    private dcpCustomerRegistrationDecoratorService: DcpCustomerRegistrationDecoratorService,
    private userRegistrationRedirectService: UserRegistrationRedirectService,
    private userRegistrationConfigService: UserRegistrationConfigService,
    private userService: DcpUserService,
    private featureTogglesService: FeatureTogglesService,
  ) {
  }

  ngOnInit(): void {
    this.validateName();
    this.initRegistrationFlow();
  }

  private getCompanies(): void {
    this.showErrors = false;
    this.domainToolService.getCompanies()
      .pipe(untilDestroyed(this))
      .subscribe((companies: DomainToolCompany[]) => {
          if (companies?.length) {
            this.companies = companies;
            this.showErrors = true;
            this.cdr.detectChanges();
          } else {
            this.navigateToB2BRegistration();
          }
        },
        () => {
          this.navigateToB2BRegistration();
        }
      );
  }

  setCompany(selectEvent): void {
    this.selectedCompany = this.companies[selectEvent.detail.item.dataset.index]
  }

  private navigateToB2BRegistration(): void {
    this.router.navigate(['/b2b-registration'], {queryParams: this.route.snapshot.queryParams, replaceUrl: true});
  }

  private validateName() {
    if (!this.userNameValidationService.getValidUserName()) {
      this.router.navigate(['/selectionType']);
    }
  }

  /**
   * If AutoUserBpRegistration FT is enabled and current customer is eligible user,
   * trigger auto-bp registration endpoint. Redirects to next page if successful.
   * Activate regular registration flow for not eligible customers
   * or when AutoUserBpRegistration FT is disabled
   *
   * @private
   */
  private initRegistrationFlow(): void {
    combineLatest([
      this.userAccountFacade.get(),
      this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.autoBpRegistration)
    ]).pipe(
      first(),
      untilDestroyed(this)
    ).subscribe(([user, isAutoBpRegistrationEnabled]: [User, boolean]) => {
      this.isRegularRegistrationFlow = !this.userService.isAutoBpCreationEligibleUser(user);
      if (isAutoBpRegistrationEnabled && !this.isRegularRegistrationFlow) {
        this.dcpCustomerRegistrationDecoratorService.autoRegisterUser(user.uid, user.scimUserId).pipe(
          first(),
          untilDestroyed(this),
        ).subscribe({
          next: r => {
            this.router.navigateByUrl((this.userRegistrationRedirectService.getCurrent() || '/')).then(() => {
              this.userRegistrationRedirectService.resetNavigationUrl();
              this.userRegistrationConfigService.resetConfig();
            });
          },
          error: e => {
            this.autoBpCreationError = {
              errorCode: e.error?.error?.code,
              isEligibleForRetry: false,
              hasError: true,
              description: e.error?.error?.message
            };
            this.cdr.detectChanges();
            throw e;
          }
        });
      } else {
        this.getCompanies();
      }
    });
  }

  goBack(){
    this.router.navigateByUrl('/selectionType');
  }
}
